// src/StaffForm.js
//
// Same dropdown pattern (Department list), plus email/phone fields.

import { useState, useEffect } from 'react';
import { API_BASE_URL } from './apiConfig';
import { formatApiError } from './formErrors';

function StaffForm({ token, staffMember, onSaved, onCancel }) {
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const [fullName, setFullName] = useState(staffMember?.full_name || '');
  const [email, setEmail] = useState(staffMember?.email || '');
  const [phoneNumber, setPhoneNumber] = useState(staffMember?.phone_number || '');
  const [department, setDepartment] = useState(staffMember?.department || '');
  const [jobTitle, setJobTitle] = useState(staffMember?.job_title || '');

  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/departments/`, {
      headers: { Authorization: `Token ${token}` },
    })
      .then((res) => res.json())
      .then((data) => {
        if (Array.isArray(data)) setDepartmentOptions(data);
      });
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    const payload = {
      full_name: fullName,
      email,
      phone_number: phoneNumber,
      // department is optional on the model (null=True, blank=True) -
      // if left unselected, send null rather than an empty string, so
      // Django's ForeignKey field doesn't try to look up a project with ID "".
      department: department || null,
      job_title: jobTitle,
    };

    const url = staffMember
      ? `${API_BASE_URL}/api/staff/${staffMember.id}/`
      : `${API_BASE_URL}/api/staff/`;
    const method = staffMember ? 'PUT' : 'POST';

    try {
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Token ${token}` },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        setError(formatApiError(await response.json()));
        setSaving(false);
        return;
      }
      onSaved();
    } catch (err) {
      setError('Could not connect to the server');
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded shadow mb-6 max-w-lg">
      <h2 className="text-lg font-bold mb-4">{staffMember ? 'Edit Staff Member' : 'New Staff Member'}</h2>
      <form onSubmit={handleSubmit}>
        <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
        <input type="text" value={fullName} onChange={(e) => setFullName(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
        <input type="text" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
        <select value={department} onChange={(e) => setDepartment(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400">
          <option value="">No Department</option>
          {departmentOptions.map((d) => (
            <option key={d.id} value={d.id}>{d.name}</option>
          ))}
        </select>

        <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
        <input type="text" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)}
          className="w-full border border-gray-300 rounded px-3 py-2 mb-4 focus:outline-none focus:ring-2 focus:ring-blue-400" />

        {error && <p className="text-red-500 text-sm mb-4 break-words">{error}</p>}

        <div className="flex gap-2">
          <button type="submit" disabled={saving}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" onClick={onCancel}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300">
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default StaffForm;
